export async function retry<T>(
  fn: () => Promise<T>,
  options: {
    maxAttempts?: number;
    initialDelay?: number;
    maxDelay?: number;
    backoffFactor?: number;
    shouldRetry?: (error: Error) => boolean; // Predicate function
  } = {},
): Promise<T> {
  const {
    maxAttempts = 3,
    initialDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2,
    shouldRetry = () => true, // Default to retrying all errors
  } = options;

  let lastError: Error | null = null;
  let delay = initialDelay;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error as Error;

      // Check if we should retry this specific error
      // Some extra explaining here: The current implementation retries on any error thrown by fn. This includes errors like 400 Bad Request or 401 Unauthorized. Retrying these errors is pointless, as they will fail every time with the same input. This can lead to unnecessary delays and wasted resources before the user is notified of a permanent failure.
      if (!shouldRetry(lastError)) {
        throw lastError;
      }

      if (attempt === maxAttempts) {
        break;
      }

      // Log intermediate attempts for better debugging
      console.warn(`Attempt ${attempt} failed. Retrying in ${delay}ms...`);

      // This "jitter" spreads out the retry attempts from different clients.
      const jitter = delay * 0.2;
      const jitteredDelay = delay + Math.random() * jitter - jitter / 2;

      await new Promise((resolve) => setTimeout(resolve, jitteredDelay));

      delay = Math.min(delay * backoffFactor, maxDelay);
    }
  }

  throw lastError;
}
